import Container from '@mui/material/Container';
import TextField from "@mui/material/TextField";
import { Localized, useLocalization } from "@fluent/react";
import { Box, Button, ButtonGroup, Dialog, DialogActions, DialogContent, DialogContentText, FormControl, Grid, InputLabel, MenuItem, Paper, Select, Skeleton, Tab, TableContainer, Typography } from "@mui/material";
import { showError, showSuccess } from "../notifications/notificationsSlice";
import { useParams, useSearchParams } from "react-router-dom";
import { unwrapResult } from "@reduxjs/toolkit";
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import SaveIcon from '@mui/icons-material/Save';
import { useAppSelector, useAppDispatch, useQueryId } from '../../app/hooks';
import { selectAllCompanies } from '../company/companiesSlice';
import { LoadCompanies } from '../../app/AppDataLoader';
import { UserStatusLabel } from './UserStatusLabel';
import { If } from '../../app/If';
import SendIcon from '@mui/icons-material/Send';
import DeleteIcon from '@mui/icons-material/Delete';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { LoadingButton, TabContext, TabList, TabPanel } from '@mui/lab';
import { APIError } from '../../app/appTypes';
import BlockIcon from '@mui/icons-material/Block';
import KeyIcon from '@mui/icons-material/Key';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { SystemEvent, loadSystemEvents } from '../systemSettings/systemEvents/systemEventsApi';
import { enUS, plPL } from '@mui/x-data-grid/locales';
import { selectSettings } from '../settings/settingsSlice';
import { useNavigate } from 'react-router-dom';
import { AppUser, archiveAppUser, blockAppUser, deleteAppUserFunc, loadAppUser, resetAppUserPassword, sendAppUserInvitation, unarchiveAppUser, unlockAppUser, updateAppUser } from './administration';

type FormTabs = "details" | "events"

export type UserFrom = {
    entity: AppUser | undefined,
}

export const EditUser = () => {
    const navigate = useNavigate()
    const queryId = useQueryId();
    const { id: paramId } = useParams(); const id = paramId ?? ''
    const { locale } = useAppSelector(selectSettings);
    const { l10n } = useLocalization();
    const [user, setUser] = useState<AppUser | undefined>(undefined)

    const [userLogin, setUserLogin] = useState<string>("");
    const [companyId, setCompanyId] = useState<string>("");
    const [firstName, setFirstName] = useState<string>("");
    const [surname, setSurname] = useState<string>("");
    const [role, setRole] = useState<string>("");
    const [phone, setPhone] = useState<string>("");
    const [status, setStatus] = useState<string>("");
    const [position, setPosition] = useState<string>("");
    const [lockedBy, setLockedBy] = useState<string>("");
    const [lockedAt, setLockedAt] = useState<Date | string>("");
    const [lockReason, setLockReason] = useState<string>("");
    const [lockReasonDialog, setLockReasonDialog] = useState<string>("");
    const [passwordDialog, setPasswordDialog] = useState<string>("");
    const dispatch = useAppDispatch();

    //errors handling
    const [firstNameError, setFirstNameError] = useState(false);
    const [surnameError, setSurnameError] = useState(false);
    const [roleError, setRoleError] = useState(false);
    const [companyError, setCompanyError] = useState(false);
    const [emailError, setEmailError] = useState("");
    const [sendInvitation, setSendInvitation] = useState(false);
    const [deleteUser, setDeleteUser] = useState(false);
    const [unlockUserDialog, setUnlockUserDialog] = useState(false);
    const [resetPasswordDialog, setResetPasswordDialog] = useState(false);
    const [blockUserDialog, setBlockUserDialog] = useState(false);
    const [archiveUserDialog, setArchiveUserDialog] = useState(false);
    const [unarchiveUserDialog, setUnarchiveUserDialog] = useState(false);
    const [systemEvents, setSystemEvents] = useState<SystemEvent[]>([]);
    
    //load prerequisities
    const allCompanies = useAppSelector(selectAllCompanies)
    const [saving, setSaving] = useState(false)
    const [loading, setLoading] = useState(false)

    const handleGoBackclick = useCallback(() => navigate(-1), [navigate])

    const getCustomLocaleText = () => {
        const defaultEnLocaleText = enUS.components.MuiDataGrid.defaultProps.localeText;

        if (locale === 'pl') {
            return plPL.components.MuiDataGrid.defaultProps.localeText;
        } else {
            return defaultEnLocaleText;
        }
    };

    useEffect(() => {
        dispatch(loadAppUser(id)).then(unwrapResult)
        .then(result => {
            setUser(result);
            setCompanyId(result.companyId);
            setUserLogin(result.userName);
            setFirstName(result.firstName);
            setSurname(result.surname);
            setRole(result.role);
            setPhone(result.phone);
            setStatus(result.status);
            setPosition(result.position);
            if(result.lockedAt !== null) {
                setLockedBy(result.lockedBy);
                setLockedAt(result.lockedAt);
                setLockReason(result.lockReason);
            }
        })
        .catch((error) => console.error(error))
        dispatch(loadSystemEvents(`?objectType=User&objectId=${id}`))
            .then(unwrapResult)
            .then(result => {
                setSystemEvents(result);
                if(queryId) setCompanyId(queryId);
            })
            .catch((error) => console.error(error))
    }, [])

    const [params, setParams] = useSearchParams()
    const tab = params.get('tab') as FormTabs || 'details'
    const setTab = (tab: FormTabs) => {
        params.set('tab', tab)
        setParams(params, { replace: true })
    }

    const handleTabChange = useCallback((_, newTab) => {
        setTab(newTab)
    }, [])

    const handleSaveClick = async () => {
        ClearErrors();
        if (!Validate()) {
            return;
        }

        if (user) {
            try {
                await dispatch(updateAppUser({
                    ...user,
                    userName: userLogin, companyId, email: userLogin,
                    firstName, surname: surname, role, phone, position
                })).unwrap();
                dispatch(showSuccess("saved"));
            } catch (error) {
                dispatch(showError("error"));
            }
        }
    }

    const validateEmail = () => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(userLogin)) {
            setEmailError("Nieprawidłowy adres e-mail");
        }
        else {
            setEmailError("");
        }
    };

    function ClearErrors() {
        setFirstNameError(false);
        setRoleError(false);
        setSurnameError(false);
        setCompanyError(false);
        setEmailError("");
    }

    function Validate() {
        let valResult = true;
        validateEmail()
        if (companyId === '') {
            valResult = false;
            setCompanyError(true);
        }
        if (role === '') {
            valResult = false;
            setRoleError(true);
        }
        if (firstName === '') {
            valResult = false;
            setFirstNameError(true);
        }
        if (surname === '') {
            valResult = false;
            setSurnameError(true);
        }
        if (emailError !== '') {
            valResult = false;

        }

        return valResult;
    }

    const handleUserLoginChange: (e: ChangeEvent<HTMLInputElement>) => void =
        useMemo(() =>
            (e) => setUserLogin(e.target.value), [setUserLogin]);


    const handleCompanyIdChange: (e: ChangeEvent<HTMLInputElement>) => void =
        useMemo(() =>
            (e) => setCompanyId(e.target.value), [setCompanyId]);

    const handleFirstNameChange: (e: ChangeEvent<HTMLInputElement>) => void =
        useMemo(() =>
            (e) => setFirstName(e.target.value), [setFirstName]);

    const handleSurnameChange: (e: ChangeEvent<HTMLInputElement>) => void =
        useMemo(() =>
            (e) => setSurname(e.target.value), [setSurname]);

    const handleRoleChange: (e: ChangeEvent<HTMLInputElement>) => void =
        useMemo(() =>
            (e) => setRole(e.target.value), [setRole]);

    const handlePhoneChange: (e: ChangeEvent<HTMLInputElement>) => void =
        useMemo(() =>
            (e) => setPhone(e.target.value), [setPhone]);

    const handlePositionChange: (e: ChangeEvent<HTMLInputElement>) => void =
        useMemo(() =>
            (e) => setPosition(e.target.value), [setPosition]);

    const handleUserLockReasonChange: (e: ChangeEvent<HTMLInputElement>) => void =
        useMemo(() =>
            (e) => setLockReasonDialog(e.target.value), [setLockReasonDialog]);

    const handlePasswordChange: (e: ChangeEvent<HTMLInputElement>) => void =
        useMemo(() =>
            (e) => setPasswordDialog(e.target.value), [setPasswordDialog]);

    const handleSendInvitationClick = useCallback(() => {
        setSendInvitation(true)
    }, [])

    const handleDeleteUserClick = useCallback(() => {
        setDeleteUser(true)
    }, [])

    const handleUnlockClick = useCallback(() => {
        setUnlockUserDialog(true)
    }, [])

    const handleResetClick = useCallback(() => {
        setResetPasswordDialog(true)
    }, [])

    const handleBlockUser = useCallback(() => {
        setBlockUserDialog(true)
    }, [])

    const handleArchiveUser = useCallback(() => {
        setArchiveUserDialog(true)
    }, [])

    const handleUnarchiveUser = useCallback(() => {
        setUnarchiveUserDialog(true)
    }, [])

    const handleConfirmArchiveUserClick = async () => {
        if (user) {
            try {
                setSaving(true)
                const result = await dispatch(archiveAppUser({ email: user.email, reason: lockReasonDialog, password: passwordDialog }));
                if (archiveAppUser.fulfilled.match(result)) {
                    result.payload as AppUser;
                    dispatch(showSuccess("saved"))
                    setStatus("ARCHIVED");
                    setLockedAt(result.payload.lockedAt ?? "");
                    setLockedBy(result.payload.lockedBy);
                    setLockReason(result.payload.lockReason);
                }
                else {
                    let errorMsg = "error"
                    if (result.payload) {
                        if (result.payload.kind === 'http') {
                            const problem = result.payload.problem
                            if (problem) {
                                errorMsg = problem.title
                            }
                        }
                    }
                    dispatch(showError(errorMsg))
                }
                setPasswordDialog("");
            }
            catch (error) {
                dispatch(showError("error"));
            }
            finally {
                setSaving(false)
                setArchiveUserDialog(false)
            }
        }
    }

    const handleConfirmUnarchiveUserClick = async () => {
        if (user) {
            try {
                setSaving(true)
                const result = await dispatch(unarchiveAppUser({ email: user.email, reason: lockReasonDialog, password: passwordDialog }));
                if (unarchiveAppUser.fulfilled.match(result)) {
                    result.payload as AppUser;
                    dispatch(showSuccess("saved"))
                    setStatus("ACTIVE");
                    setLockReason("");
                }
                else {
                    let errorMsg = "error"
                    if (result.payload) {
                        if (result.payload.kind === 'http') {
                            const problem = result.payload.problem
                            if (problem) {
                                errorMsg = problem.title
                            }
                        }
                    }
                    dispatch(showError(errorMsg))
                }
                setPasswordDialog("");
            }
            catch (error) {
                dispatch(showError("error"));
            }
            finally {
                setSaving(false)
                setUnarchiveUserDialog(false)
            }
        }
    }

    const handleBlockUserClick = async () => {
        if (user) {
            try {
                setSaving(true)
                const result = await dispatch(blockAppUser({ email: user.email, lockreason: lockReasonDialog }));
                if (blockAppUser.fulfilled.match(result)) {
                    result.payload as AppUser;
                    dispatch(showSuccess("saved"))
                    setStatus("LOCKED");
                    setLockedBy(result.payload.lockedBy);
                    setLockedAt(result.payload.lockedAt ? new Date(result.payload.lockedAt) : "");
                    setLockReason(result.payload.lockReason);
                }
                else {
                    let errorMsg = "error"
                    if (result.payload) {
                        if (result.payload.kind === 'http') {
                            const problem = result.payload.problem
                            if (problem) {
                                errorMsg = problem.title
                            }
                        }
                    }
                    dispatch(showError(errorMsg))
                }
            }
            catch (error) {
                dispatch(showError("error"));
            }
            finally {
                setSaving(false)
                setBlockUserDialog(false)
            }
        }
        setBlockUserDialog(false)
    }


    const handleResetPasswordClick = async () => {
        if (user) {
            try {
                setSaving(true)
                await dispatch(resetAppUserPassword({ email: user.email })).unwrap();
                dispatch(showSuccess("saved"))
            }
            catch (error) {
                dispatch(showError("error"));
            }
            finally {
                setSaving(false)
                setResetPasswordDialog(false)
            }
        }
        setResetPasswordDialog(false)
    }

    const handleUnlockUserClick = async () => {
        if (user) {
            try {
                setSaving(true)
                await dispatch(unlockAppUser({ email: user.email })).unwrap();

                dispatch(showSuccess("saved"))

                setStatus("ACTIVE");
                setLockedBy("");
                setLockedAt("");
                setLockReason("");
            }
            catch (error) {
                const apierror = error as APIError;
                if (apierror.kind==="http" && apierror.problem?.title){
                    dispatch(showError(apierror.problem?.title));
                }
                else{
                    dispatch(showError("error"));
                }
            }
            finally {
                setSaving(false)
                setUnlockUserDialog(false)
            }
        }
        setUnlockUserDialog(false)
    }

    const handleConfirmSendInvitationClick = async () => {
        if (user) {
            try {
                setSaving(true)
                const result = await dispatch(sendAppUserInvitation({email: user.email}));

                if (sendAppUserInvitation.fulfilled.match(result)) {
                    dispatch(showSuccess("saved"))
                    setStatus("INVITED");
                } else {
                    dispatch(showError("error"))
                }
            }
            catch (error) {
                dispatch(showError("error"));
            }
            finally {
                setSaving(false)
                setSendInvitation(false)
            }
        }
        setSendInvitation(false)
    }

    const handleConfirmDeleteUserClick = async () => {
        if (user) {
            try {
                setSaving(true)
                const result = await dispatch(deleteAppUserFunc({email: user.email, password: passwordDialog }));
                if (deleteAppUserFunc.fulfilled.match(result)) {
                    result.payload as AppUser;
                    dispatch(showSuccess("saved"))
                    handleGoBackclick();
                }
                else {
                    let errorMsg = "error"
                    if (result.payload) {
                        if (result.payload.kind === 'http') {
                            const problem = result.payload.problem
                            if (problem) {
                                errorMsg = problem.title
                            }
                        }
                    }
                    dispatch(showError(errorMsg))
                }
            }
            catch (error) {
                dispatch(showError("error"));
            }
            finally {
                setSaving(false)
                setDeleteUser(false)
            }
        }
        setDeleteUser(false)
    }

    const handleCancelSendInvitationClick = async () => {
        setSendInvitation(false)
    }

    const handleCancelDeleteUserClick = async () => {
        setDeleteUser(false)
    }

    const handleCanceUnlockUserClick = async () => {
        setUnlockUserDialog(false)
    }

    const handleCanceResetPasswordClick = async () => {
        setResetPasswordDialog(false)
    }

    const handleCancelBlockUserClick = async () => {
        setBlockUserDialog(false)
    }

    const handleCancelArchiveUserClick = async () => {
        setArchiveUserDialog(false)
    }

    const handleCancelUnarchiveUserClick = async () => {
        setUnarchiveUserDialog(false)
    }

    const columns: GridColDef<SystemEvent, any, any>[] = [
        {
            field: 'createdBy',
            headerName: l10n.getString("created-by", null, "_Utworzył"),
            width: 250
        },
        {
            field: 'eventType',
            headerName: l10n.getString("system-events-type", null, "Zdarzenie"),
            flex: 1,
        },
        {
            field: 'eventDate',
            headerName: l10n.getString("system-events-date", null, "Data zdarzenia"),
            flex: 1,
            valueGetter: (_, row) => {
                if(row.eventDate !== null) return new Date(row.eventDate).toLocaleString();
            }
        },
        {
            field: 'description',
            headerName: l10n.getString("system-events-description", null, "Opis"),
            flex: 1,
        },
        {
            field: 'expired',
            headerName: l10n.getString("status", null, "Status"),
            width: 100,
            valueGetter: (_, row) => {
                if(row.eventType === "SendInvitation" && row.eventDate !== null && (new Date().getTime() - new Date(row.eventDate).getTime()) / (1000 * 60 * 60) >= 24) return l10n.getString("system-events-expired", null, "Wygasło")
            }
        }
    ]


    return (
        <Container maxWidth="md" sx={{
            paddingTop: 2,
            paddingBottom: 2,
        }}>
            <TabContext value={tab}>
                <TabList onChange={handleTabChange}>
                    <Tab
                        key={0}
                        value={"details"}
                        label={<Localized id="details">Szczegóły</Localized>} />
                    <Tab
                        key={1}
                        value={"events"}
                        label={<Localized id="system-events-history">Historia zdarzeń</Localized>} />
                </TabList>
                <TabPanel value="details">
                    <Paper>
                        <Grid container spacing={2} sx={{ padding: 2 }}>
                            <Grid item xs={8}>
                                <TextField
                                    fullWidth
                                    required
                                    disabled={user && status === "ACTIVE"}
                                    inputProps={{ maxLength: 50 }}
                                    value={userLogin}
                                    onChange={handleUserLoginChange}
                                    onBlur={validateEmail}
                                    error={!!emailError}
                                    helperText={emailError}
                                    label={<Localized id="user-login-email">_Login / Email</Localized>}>
                                </TextField>
                            </Grid>
                            <Grid item xs={4}>
                                <Typography >
                                    <UserStatusLabel status={status}></UserStatusLabel>
                                </Typography >
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    required
                                    fullWidth
                                    inputProps={{ maxLength: 50 }}
                                    value={firstName}
                                    error={firstNameError}
                                    onChange={handleFirstNameChange}
                                    label={<Localized id="user-first-name">Imię</Localized>}>
                                </TextField>
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    required
                                    fullWidth
                                    value={surname}
                                    error={surnameError}
                                    inputProps={{ maxLength: 100 }}
                                    onChange={handleSurnameChange}
                                    label={<Localized id="user-last-name">Nazwisko</Localized>}>
                                </TextField>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    select
                                    required
                                    sx={{ minWidth: 256 }}
                                    value={role}
                                    error={roleError}
                                    onChange={handleRoleChange}
                                    label={<Localized id="user-role" vars={{ num : 0 }}>Rola</Localized>} >
                                    {["support_agent", "admin", "user"].map(stage => <MenuItem key={stage} value={stage}>{stage}</MenuItem>)}
                                </TextField>
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    value={phone}
                                    inputProps={{ maxLength: 20 }}
                                    onChange={handlePhoneChange}
                                    label={<Localized id="user-phone">Phone</Localized>}>
                                </TextField>
                            </Grid>
                            <Grid item xs={12}>
                                <LoadCompanies component={<Skeleton variant="rectangular" />}>
                                    <TextField
                                        select
                                        fullWidth
                                        required
                                        value={companyId}
                                        error={companyError}
                                        onChange={handleCompanyIdChange}
                                        label={<Localized id="user-company">Firma</Localized>}>
                                        {allCompanies.map((company) => <MenuItem key={company.id} value={company.id}>{company.name}</MenuItem>)}
                                    </TextField>
                                </LoadCompanies>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    value={position}
                                    inputProps={{ maxLength: 200 }}
                                    onChange={handlePositionChange}
                                    label={<Localized id="user-position">Stanowisko</Localized>}>
                                </TextField>
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>
                                        <Localized id="user-department">_Dział</Localized>
                                    </InputLabel>
                                    <Select
                                        label={l10n.getString("user-department", null, "_Dział")}
                                    >
                                        {[l10n.getString("user-department-1", null, "_"), l10n.getString("user-department-2", null, "_"), l10n.getString("user-department-3", null, "_"), l10n.getString("user-department-4", null, "_"), l10n.getString("user-department-5", null, "_"), l10n.getString("user-department-6", null, "_"), l10n.getString("user-department-7", null, "_")].map((role) => <MenuItem key={role} value={role}>{role}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <If condition={lockReason !== ""}>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        value={lockedBy}
                                        aria-readonly
                                        disabled={true}
                                        label={status === "ARCHIVED" ? <Localized id="user-archived-by">_Zarchiwizowany przez</Localized> : <Localized id="user-locked-by">_Zablokowany przez</Localized>}>
                                    </TextField>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        value={new Date(lockedAt).toLocaleString()}
                                        aria-readonly
                                        disabled={true}
                                        label={status === "ARCHIVED" ? <Localized id="user-archiving-date">_Data archiwizacji</Localized> : <Localized id="user-lock-date">Data blokady</Localized>}>
                                    </TextField>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        value={lockReason}
                                        multiline
                                        rows={4}
                                        aria-readonly
                                        disabled={true}
                                        label={status === "ARCHIVED" ? <Localized id="user-archiving-reason">_Powód archiwizacji</Localized> : <Localized id="user-lock-reason">Powód blokady</Localized>}>
                                    </TextField>
                                </Grid>
                            </If>
                            <Grid item xs={4}>
                                <ButtonGroup color="secondary" variant="contained">
                                    <Button startIcon={<SaveIcon />} onClick={handleSaveClick}>
                                        <Localized id="save">Zapisz</Localized>
                                    </Button>
                                    <Button onClick={handleGoBackclick} color="secondary" variant="outlined">
                                        <Localized id="back">Wróć</Localized>
                                    </Button>
                                </ButtonGroup>
                            </Grid>
                            <Grid item xs={8}>
                                <Box display="flex" justifyContent="flex-end">
                                    <If condition={status === "NOT_CONFIRMED" || status === "INVITED"}>
                                        <Button onClick={handleSendInvitationClick} color="primary" variant="outlined" endIcon={<SendIcon color='secondary' />}>
                                            <Localized id="invite">Zaproś</Localized>
                                        </Button>
                                        <Button onClick={handleDeleteUserClick} color="primary" variant="outlined" endIcon={<DeleteIcon color='secondary' />}>
                                            <Localized id="delete">Usuń</Localized>
                                        </Button>
                                    </If>
                                    <If condition={status === "LOCKED"}>
                                        <Button onClick={handleUnlockClick} color="primary" variant="outlined" endIcon={<LockOpenIcon color='secondary' />}>
                                            <Localized id="unlock">Odblokuj</Localized>
                                        </Button>
                                    </If>
                                    <If condition={status === "LOCKED" || status === "ACTIVE"}>
                                        <Button onClick={handleArchiveUser} color="primary" variant="outlined" endIcon={<LockOpenIcon color='secondary' />}>
                                            <Localized id="user-archive">Archiwizuj</Localized>
                                        </Button>
                                        <Button onClick={handleResetClick} color="primary" variant="outlined" endIcon={<KeyIcon color='secondary' />}>
                                            <Localized id="reset-password">Resetuj hasło</Localized>
                                        </Button>
                                    </If>
                                    <If condition={status === "ACTIVE"}>
                                        <Button onClick={handleBlockUser} color="primary" variant="outlined" endIcon={<BlockIcon color='error' />}>
                                            <Localized id="user-block">Zablokuj</Localized>
                                        </Button>
                                    </If>
                                    <If condition={status === "ARCHIVED"}>
                                        <Button onClick={handleUnarchiveUser} color="primary" variant="outlined" endIcon={<LockOpenIcon color='secondary' />}>
                                            <Localized id="user-unarchive">_Przywróć</Localized>
                                        </Button>
                                    </If>
                                </Box>
                            </Grid>
                        </Grid>
                    </Paper>
                </TabPanel>
                <TabPanel value="events">
                    <TableContainer component={Paper}>
                        <DataGrid localeText={getCustomLocaleText()}
                            style={{ minHeight: 100, height: 'calc(100vh - 240px)' }}
                            rows={systemEvents}
                            columns={columns}
                            initialState={{
                                sorting: { sortModel: [{ field: 'eventDate', sort: 'desc' }] }
                            }}
                        />
                    </TableContainer>
                </TabPanel>
            </TabContext>
            <Dialog open={sendInvitation}>
                <DialogContent>
                    <DialogContentText>
                        <Localized id="user-send-invitation">
                            <span>Do wybranego użytkownika zostanie wysłany mail powitalny, czy kontunuować?</span>
                        </Localized>
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <LoadingButton loading={saving} onClick={handleConfirmSendInvitationClick}>
                        <Localized id="yes"><span>Tak</span></Localized>
                    </LoadingButton>
                    <LoadingButton loading={saving} onClick={handleCancelSendInvitationClick}>
                        <Localized id="no"><span>Nie</span></Localized>
                    </LoadingButton>
                </DialogActions>
            </Dialog>
            <Dialog open={deleteUser}>
                <DialogContent>
                    <DialogContentText>
                        <Localized id="user-delete">
                            <span>Czy na pewno chcesz usunąć tego użytkownika?</span>
                        </Localized>

                        <TextField
                            sx={{ marginTop: "25px" }}
                            fullWidth
                            value={passwordDialog}
                            required
                            onChange={handlePasswordChange}
                            type={"password"}
                            inputProps={{ maxLength: 100 }}
                            label={<Localized id="password">Hasło</Localized>}
                            helperText={<Localized id="autorization-password-required"><span>Wymagana autoryzacja hasłem</span></Localized>}>
                        </TextField>
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <LoadingButton loading={saving} onClick={handleConfirmDeleteUserClick}>
                        <Localized id="yes"><span>Tak</span></Localized>
                    </LoadingButton>
                    <LoadingButton loading={saving} onClick={handleCancelDeleteUserClick}>
                        <Localized id="no"><span>Nie</span></Localized>
                    </LoadingButton>
                </DialogActions>
            </Dialog>
            <Dialog open={unlockUserDialog}>
                <DialogContent>
                    <DialogContentText>
                        <Localized id="user-unlock-question">
                            <span>Czy odblokować wskazanego użytkownika?</span>
                        </Localized>
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <LoadingButton loading={saving} onClick={handleUnlockUserClick}>
                        <Localized id="yes"><span>Tak</span></Localized>
                    </LoadingButton>
                    <LoadingButton loading={saving} onClick={handleCanceUnlockUserClick}>
                        <Localized id="no"><span>Nie</span></Localized>
                    </LoadingButton>
                </DialogActions>
            </Dialog>
            <Dialog open={resetPasswordDialog}>
                <DialogContent>
                    <DialogContentText>
                        <Localized id="user-reset-password-question">
                            <span>Czy zresetować hasło wskazanego użytkownika?</span>
                        </Localized>
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <LoadingButton loading={saving} onClick={handleResetPasswordClick}>
                        <Localized id="yes"><span>Tak</span></Localized>
                    </LoadingButton>
                    <LoadingButton loading={saving} onClick={handleCanceResetPasswordClick}>
                        <Localized id="no"><span>Nie</span></Localized>
                    </LoadingButton>
                </DialogActions>
            </Dialog>
            <Dialog open={blockUserDialog}>
                <DialogContent>
                    <DialogContentText>
                        <Localized id="user-block-question">
                            <span>Czy zablokować użytkownika?</span>
                        </Localized>
                        <TextField
                            fullWidth
                            value={lockReasonDialog}
                            required
                            onChange={handleUserLockReasonChange}
                            type={"text"}
                            multiline
                            rows={4}
                            inputProps={{ maxLength: 1000 }}
                            label={<Localized id="user-lock-reason">Powód blokady</Localized>}>
                        </TextField>
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <LoadingButton loading={saving} onClick={handleBlockUserClick}>
                        <Localized id="yes"><span>Tak</span></Localized>
                    </LoadingButton>
                    <LoadingButton loading={saving} onClick={handleCancelBlockUserClick}>
                        <Localized id="no"><span>Nie</span></Localized>
                    </LoadingButton>
                </DialogActions>
            </Dialog>
            <Dialog open={archiveUserDialog}>
                <DialogContent>
                    <DialogContentText>
                        <Localized id="user-archiving-question">
                            <span>Czy na pewno chcesz zarchiwizować użytkownika? Zarchwizowanie użytkownika spowoduje utratę dostępu do aplikacji</span>
                        </Localized>
                        <TextField
                            fullWidth
                            value={lockReasonDialog}
                            required
                            onChange={handleUserLockReasonChange}
                            type={"text"}
                            multiline
                            rows={4}
                            inputProps={{ maxLength: 1000 }}
                            label={<Localized id="user-archive-reason">Powód archiwizacji</Localized>}>
                        </TextField>
                        <TextField
                            sx={{ marginTop: "25px" }}
                            fullWidth
                            value={passwordDialog}
                            required
                            onChange={handlePasswordChange}
                            type={"password"}
                            inputProps={{ maxLength: 100 }}
                            label={<Localized id="password">Hasło</Localized>}
                            helperText={<Localized id="autorization-password-required"><span>Wymagana autoryzacja hasłem</span></Localized>}>
                        </TextField>
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <LoadingButton loading={saving} onClick={handleConfirmArchiveUserClick}>
                        <Localized id="yes"><span>Tak</span></Localized>
                    </LoadingButton>
                    <LoadingButton loading={saving} onClick={handleCancelArchiveUserClick}>
                        <Localized id="no"><span>Nie</span></Localized>
                    </LoadingButton>
                </DialogActions>
            </Dialog>
            <Dialog open={unarchiveUserDialog}>
                <DialogContent>
                    <DialogContentText>
                        <Localized id="user-unarchiving-question">
                            <span>_Czy na pewno chcesz przywrócić użytkownika?</span>
                        </Localized>
                        <TextField
                            fullWidth
                            value={lockReasonDialog}
                            required
                            onChange={handleUserLockReasonChange}
                            type={"text"}
                            multiline
                            rows={4}
                            inputProps={{ maxLength: 1000 }}
                            label={<Localized id="user-unarchiving-reason">_Powód przywrócenia</Localized>}>
                        </TextField>
                        <TextField
                            sx={{ marginTop: "25px" }}
                            fullWidth
                            value={passwordDialog}
                            required
                            onChange={handlePasswordChange}
                            type={"password"}
                            inputProps={{ maxLength: 100 }}
                            label={<Localized id="password">_Hasło</Localized>}
                            helperText={<Localized id="autorization-password-required"><span>Wymagana autoryzacja hasłem</span></Localized>}>
                        </TextField>
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <LoadingButton loading={saving} onClick={handleConfirmUnarchiveUserClick}>
                        <Localized id="yes"><span>Tak</span></Localized>
                    </LoadingButton>
                    <LoadingButton loading={saving} onClick={handleCancelUnarchiveUserClick}>
                        <Localized id="no"><span>Nie</span></Localized>
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </Container>
    );
}
